import {createResource, getResource, updateResource} from "./common"
import jsonMergePatch from "json-merge-patch"
import {has} from "lodash"
import {removeEmptyAttributes} from "../utilities/objects"
import {AccountStatus, AccountType, findAccounts, FindAccountsRequestParam} from "./account"

export type WalletTerms = {
	type: "walletTerms"
	id: string
	attributes: {
		createdAt: Date
		status: string
		statusChangedAt: Date
		orgId: string
		bankId: string
		bankRoutingId: string | null
		transactionMonitoringSettingsId: string | null
		orgGLAccountId: string
		orgFBOAccountId: string
		name: string
		incomingACHFee: number
		outgoingACHFee: number
		achLimits: {
			dailyDebit: number
			dailyCredit: number
			monthlyDebit: number
			monthlyCredit: number
			dailyDebitSoft: number
			monthlyDebitSoft: number
		}
		isAchDebitEnabled: boolean
		isAchCreditEnabled: boolean
		isBookPaymentsEnabled: boolean
		isCheckDepositEnabled: boolean
		checkDepositLimits: {
			daily: number
			monthly: number
			dailySoft: number
			monthlySoft: number
		}
		checkWritingEnabled: boolean
		checkWritingOriginationEnabled: boolean
		realTimePaymentsEnabled?: boolean
		checkPaymentLimits: {
			daily: number
			monthly: number
			dailySoft: number
			monthlySoft: number
		}
		isWirePaymentsEnabled: boolean
		minimumIdentityVerificationLevel: string
		wireLimits: {
			daily: number
			monthly: number
			dailySoft: number
			monthlySoft: number
		}
		incomingWireFee: number
		outgoingWireFee: number
		dailyCardWithdrawalLimit: number
		dailyCardDepositLimit: number
		dailyCardPurchaseLimit: number
		dailyCardTransactionLimit: number
		cardProduct: CardProduct | null
		checkDepositClearingDays: number
		checkDepositMinClearingDays: number | null
		checkDepositMaxClearingDays: number | null
		achDebitClearingDays: number
		achDebitMinClearingDays: number | null
		achDebitMaxClearingDays: number | null
		enableDynamicClearingDaysAchDebit: boolean
		enableCheckDepositDynamicClearingDays: boolean
		isAchDebitFraudPredictEnabled: boolean
		additionalVerificationThreshold: number | null
		interestTerms: InterestTerms
		isAutoCloseEnabled: boolean
		autoCloseAfter: number
		overdraftSettings: OverdraftSettings
		interestSettingsId: string | null
		isSweepEnabled: boolean
		enableCustomerInterestFormula: boolean
		customerInterestFormula: string | null
		bankName: string | null
	}
	relationships?: {
		org?: {
			data: {
				type: "org"
				id: string
			}
		}
		bank?: {
			data: {
				type: "bank"
				id: string
			}
		}
		bankRouting?: {
			data: {
				type: "bankRouting"
				id: string
			}
		}
		revenueAccount: {
			data: {
				type: "account"
				id: string
			}
		}
		transactionMonitoringSettings?: {
			data: {
				type: "transactionMonitoringSettings"
				id: string
			}
		}
		interestSettings?: {
			data: {
				type: "interestSetting"
				id: string
			}
		}
		orgGLAccountId: {
			data: {
				type: "orgGLAccountId"
				id: string
			}
		}
		orgFBOAccountId: {
			data: {
				type: "orgFBOAccountId"
				id: string
			}
		}
	}
}

export type InterestTerms = {
	orgShare: number | undefined
	customerShare: number | undefined
	sponsoredInterestFormula: string | undefined
	interestFormula?: string | undefined
	interestRate?: number | undefined
}

export type OverdraftSettings = {
	enableOverdraft: boolean
	overdraftDaysLimit: number | null
}

export enum Status {
	Enabled = "Enabled",
	Disabled = "Disabled",
}

export type CardProduct = {
	cardQualifier: string | null
	individualBinId?: string | null
	businessBinId?: string | null
	limitGroup: string | null
	maxNumberOfPhysicalCards: number | null
	maxNumberOfVirtualCards: number | null
	programmaticPurchaseAuthorizationEnabled: boolean | null
	programmaticPurchaseAuthorizationTimeoutStrategy?: string | null
	programmaticCardTransactionAuthorizationEnabled: boolean | null
	programmaticCardTransactionAuthorizationTimeoutStrategy?: string | null
	programmaticAtmAuthorizationEnabled: boolean | null
	programmaticAtmAuthorizationTimeoutStrategy?: string | null
	programmaticPurchaseAuthorizationIncludeCredit?: boolean | null
	programmaticCardTransactionAuthorizationIncludeCredit?: boolean | null
	programmaticAdviceAuthorizationEnabled?: boolean | null
	orgInternationalServiceFee?: number | null
}

export type BIN = {
	bin: string
	institutionId: string
}

export type LimitGroupType = keyof typeof LimitGroup

export enum LimitGroup {
	LZ1706 = "Decline All",
	LZ1704 = "Up To 50",
	LZ1705 = "Up To 100",
	LZ1701 = "Up To 500",
	LZ1702 = "Up To 1000",
	LZ1703 = "Up To 2000",
	LZ1708 = "Up To 15000",
}

export type TimeoutStrategyType = keyof typeof TimeoutStrategy

export enum TimeoutStrategy {
	Approve = "Approve",
	Decline = "Decline",
}

export type MinimumIdentityVerificationLevelType = keyof typeof MinimumIdentityVerificationLevel

export enum MinimumIdentityVerificationLevel {
	Full = "Full",
	None = "None",
	NotRequired = "Not Required",
}

export async function findWalletTerms(
	accessToken: string,
	offset: number,
	limit: number,
	orgs?: string[],
	banks?: string[],
	searchQuery?: string
) {
	const orgFilter = orgs && orgs.length > 0 ? orgs : null
	const bankFilter = banks && banks.length > 0 ? banks : null
	const query = {
		page: {
			limit,
			offset,
		},
		filter: {
			status,
			orgs: orgFilter,
			banks: bankFilter,
			query: searchQuery != "" ? searchQuery : null,
		},
		include: "bank,org",
	}

	return await getResource<Array<WalletTerms>>("wallet-terms", accessToken, query)
}

export async function getWalletTerms(accessToken: string, id: string) {
	const query = {
		extraFields: {
			interestTerms: true,
		},
	}
	const result = await getResource<WalletTerms>(`wallet-terms/${id}`, accessToken, query)

	return result.map((v) => v.data)
}

export async function createWalletTerms(
	accessToken: string,
	name: string,
	orgId: string,
	bankId: string,
	bankRoutingId: string,
	incomingACHFee: number,
	outgoingACHFee: number,
	dailyACHDebitLimit: number,
	dailyACHCreditLimit: number,
	monthlyACHDebitLimit: number,
	monthlyACHCreditLimit: number,
	dailyACHDebitSoftLimit: number,
	monthlyACHDebitSoftLimit: number,
	dailyCardWithdrawalLimit: number,
	dailyCardDepositLimit: number,
	dailyCardPurchaseLimit: number,
	dailyCardTransactionLimit: number,
	achDebitClearingDays: number,
	incomingWireFee: number,
	outgoingWireFee: number,
	cardProduct: CardProduct | undefined,
	transactionMonitoringSettingsId: string,
	interestTerms: InterestTerms,
	checkDepositClearingDays: number,
	checkDepositMinClearingDays: number | undefined | null,
	checkDepositMaxClearingDays: number | undefined | null,
	dailyCheckDepositLimit: number,
	monthlyCheckDepositLimit: number,
	dailyCheckDepositSoftLimit: number,
	monthlyCheckDepositSoftLimit: number,
	isAutoCloseEnabled: boolean,
	autoCloseAfter: number,
	overdraftSettings: OverdraftSettings,
	dailyWireLimit: number,
	monthlyWireLimit: number,
	dailyWireSoftLimit: number,
	monthlyWireSoftLimit: number,
	dailyCheckPaymentLimit: number | undefined | null,
	monthlyCheckPaymentLimit: number | undefined | null,
	dailyCheckPaymentSoftLimit: number | undefined | null,
	monthlyCheckPaymentSoftLimit: number | undefined | null,
	checkWritingEnabled: boolean,
	interestSettingsId: string | undefined | null,
	enableCheckDepositDynamicClearingDays: boolean | undefined,
	enableDynamicClearingDaysAchDebit: boolean | undefined,
	achDebitMinClearingDays: number | undefined | null,
	achDebitMaxClearingDays: number | undefined | null,
	isAchDebitFraudPredictEnabled: boolean | undefined,
	isSweepEnabled: boolean | undefined,
	checkWritingOriginationEnabled: boolean,
	realTimePaymentsEnabled: boolean,
	additionalVerificationThreshold: number | undefined | null,
	enableCustomerInterestFormula: boolean | undefined,
	customerInterestFormula: string | undefined | null,
	orgGlId: string,
	orgFboId: string,
	minimumIdentityVerificationLevel: string
) {
	const interestSettings = interestSettingsId
		? {
				data: {
					type: "interestSettings",
					id: interestSettingsId,
				},
		  }
		: undefined
	const result = await createResource<WalletTerms>("wallet-terms", accessToken, {
		type: "walletTerms",
		attributes: {
			name,
			incomingACHFee,
			outgoingACHFee,
			dailyACHDebitLimit,
			dailyACHCreditLimit,
			monthlyACHDebitLimit,
			monthlyACHCreditLimit,
			dailyACHDebitSoftLimit,
			monthlyACHDebitSoftLimit,
			dailyCardWithdrawalLimit,
			dailyCardDepositLimit,
			dailyCardPurchaseLimit,
			dailyCardTransactionLimit,
			achDebitClearingDays,
			cardProduct,
			incomingWireFee,
			outgoingWireFee,
			checkDepositClearingDays,
			checkDepositMinClearingDays,
			checkDepositMaxClearingDays,
			dailyCheckDepositLimit,
			monthlyCheckDepositLimit,
			dailyCheckDepositSoftLimit,
			monthlyCheckDepositSoftLimit,
			isAutoCloseEnabled,
			autoCloseAfter,
			overdraftSettings,
			dailyWireLimit,
			monthlyWireLimit,
			dailyWireSoftLimit,
			monthlyWireSoftLimit,
			dailyCheckPaymentLimit,
			monthlyCheckPaymentLimit,
			dailyCheckPaymentSoftLimit,
			monthlyCheckPaymentSoftLimit,
			checkWritingEnabled,
			enableDynamicClearingDaysAchDebit,
			enableCheckDepositDynamicClearingDays,
			achDebitMinClearingDays,
			achDebitMaxClearingDays,
			isAchDebitFraudPredictEnabled,
			isSweepEnabled,
			checkWritingOriginationEnabled,
			realTimePaymentsEnabled,
			additionalVerificationThreshold,
			enableCustomerInterestFormula,
			customerInterestFormula,
			minimumIdentityVerificationLevel,
		},
		relationships: {
			org: {
				data: {
					type: "org",
					id: orgId,
				},
			},
			bankRouting: {
				data: {
					type: "bankRouting",
					id: bankRoutingId,
				},
			},
			bank: {
				data: {
					type: "bank",
					id: bankId,
				},
			},
			transactionMonitoringSettings: {
				data: {
					type: "transactionMonitoringSettings",
					id: transactionMonitoringSettingsId,
				},
			},
			interestSettings,
			orgGLAccountId: {
				data: {
					type: "orgGLAccountId",
					id: orgGlId,
				},
			},
			orgFBOAccountId: {
				data: {
					type: "orgFBOAccountId",
					id: orgFboId,
				},
			},
		},
	})
	return result.map((v) => v.data)
}

export async function updateWalletTerms(
	isUnitUser: boolean,
	accessToken: string,
	walletTerms: WalletTerms,
	cardProduct: CardProduct | null,
	interestTerms: InterestTerms,
	editedAttributes: Record<string, any>,
	editedRelationships: Record<string, any>
) {
	const walletTermsAttributes = {...walletTerms.attributes, ...editedAttributes}
	const newData = removeEmptyAttributes(walletTermsAttributes)

	let cardProductPatch: {} | null = null

	if (isUnitUser && cardProduct) {
		cardProductPatch = jsonMergePatch.generate(walletTerms.attributes.cardProduct ?? {}, cardProduct)

		if (cardProductPatch && has(cardProductPatch, "individualBin")) {
			cardProductPatch = Object.assign(cardProductPatch ?? {}, {individualBin: cardProduct.individualBinId})
		}

		if (cardProductPatch && has(cardProductPatch, "businessBin")) {
			cardProductPatch = Object.assign(cardProductPatch ?? {}, {businessBin: cardProduct.businessBinId})
		}
	}

	const attributesPatch = jsonMergePatch.generate(walletTerms.attributes, newData)
	const overdraftSettings = isUnitUser ? {overdraftSettings: editedAttributes.overdraftSettings} : {}
	const patch = {
		...attributesPatch,
		interestTerms,
		...(isUnitUser ? {cardProduct: cardProductPatch} : {}),
		...(isUnitUser ? overdraftSettings : {}),
	}
	type RelationshipData = {
		type: string
		id: string
	}
	type Relationships = {
		interestSettings: {
			data: RelationshipData
		}
		transactionMonitoringSettings?: {
			data: RelationshipData
		}
	}
	const relationships: Relationships = {
		interestSettings: {
			data: {
				type: "interestSetting",
				id: editedRelationships.interestSettingsId,
			},
		},
		...(isUnitUser && editedRelationships.transactionMonitoringSettingsId
			? {
					transactionMonitoringSettings: {
						data: {
							type: "transactionMonitoringSettings",
							id: editedRelationships.transactionMonitoringSettingsId,
						},
					},
			  }
			: {}),
	}
	const patchData = {
		type: "updateWalletTerms",
		attributes: patch,
		relationships,
	}
	const result = await updateResource<WalletTerms>(`wallet-terms/${walletTerms.id}`, accessToken, patchData)
	return result.map((v) => v.data)
}

export async function disableWalletTerms(accessToken: string, walletTerms: WalletTerms) {
	return await createResource<WalletTerms>(`wallet-terms/${walletTerms.id}/disable`, accessToken, {})
}

export async function enableWalletTerms(accessToken: string, walletTerms: WalletTerms) {
	return await createResource<WalletTerms>(`wallet-terms/${walletTerms.id}/enable`, accessToken, {})
}

export async function findActiveAssociatedAccounts(accessToken: string, walletAccountId: number) {
	const request: FindAccountsRequestParam = {
		accessToken,
		offset: 0,
		limit: 10000,
		walletTermsId: walletAccountId,
		types: [AccountType.wallet],
		status: [AccountStatus.Open],
		requestType: "json",
	}
	return await findAccounts(request)
}

export async function findActiveAccountsByType(accessToken: string, accountType: AccountType) {
	const request: FindAccountsRequestParam = {
		accessToken,
		offset: 0,
		limit: 10000,
		types: [accountType],
		status: [AccountStatus.Open],
		requestType: "json",
	}
	return await findAccounts(request)
}
